<template>
  <div class="container-fluid px-4 py-3">
    <!-- Filter Section -->
    <data-filter
      ref="dataFilter"
      :on-apply-filter="onApplyFilter"
      hide-report
      :minute="true"
      :hide-interval="true"
    />

    <!-- Main Content -->
    <div class="row mt-3">
      <!-- Left: Data Table -->
      <div class="col-lg-5 col-md-12 mb-3">
        <div class="border rounded bg-white p-3" style="height: calc(100vh - 220px); min-height: 400px; display: flex; flex-direction: column;">
          <h6 class="border-bottom pb-2 mb-3 font-weight-bold">
            <i class="fas fa-table mr-2"></i>Satellite Datalog ({{ currAsset?.massName || 'No asset selected' }})
          </h6>

          <div class="table-responsive flex-grow-1" style="overflow-y: auto;">
            <table class="table table-striped table-sm table-hover table-bordered text-center">
              <thead class="thead-light" style="top: 0; z-index: 2;">
                <tr>
                  <th style="min-width: 140px;">GPS Time</th>
                  <th style="min-width: 80px;">Latitude</th>
                  <th style="min-width: 80px;">Longitude</th>
                  <th style="min-width: 80px;">Heading</th>
                  <th style="min-width: 80px;">Port RPM</th>
                  <th style="min-width: 80px;">Stb RPM</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="combinedData.length === 0">
                  <td colspan="6" class="text-center text-muted py-5">
                    <i class="fas fa-inbox fa-3x mb-3 d-block"></i>
                    No data available. Please select an asset and apply filter.
                  </td>
                </tr>
                <tr v-for="(row, index) in combinedData" :key="index">
                  <td>{{ formatToBrowserTime(row.gpsDatetime) }}</td>
                  <td>{{ $fmt(row.latitude, 6) || '-' }}</td>
                  <td>{{ $fmt(row.longitude, 6) || '-' }}</td>
                  <td>{{ formatHeading(row.heading) }}</td>
                  <td>{{ row.portRpm ?? '-' }}</td>
                  <td>{{ row.stbRpm ?? '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Right: Map -->
      <div class="col-lg-7 col-md-12">
        <div class="border rounded bg-white p-0" style="height: calc(100vh - 220px); min-height: 400px; overflow: hidden;">
          <div id="satelliteMap" style="height: 100%; width: 100%;"></div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { fromApiDatetime, DISPLAY_DATETIME } from '@/util/apiDatetime';
/* eslint-disable no-undef */
import DataFilter from '@/components/DataFilter';
import SeriesSvc from '@/services/SeriesSvc';
import MapService from '@/views/map/services/mapService';
import { convertDecimalToDMS } from '@/views/map/utils/coordinates';
import { initAssetSummary } from '@/util/summary/asset-summary-loader';
import filterDeepLink from '@/mixins/filterDeepLink';
import markerOnline from '@/assets/images/map/online-marker.svg';
import headingOnline from '@/assets/images/map/online-heading.svg';
import { formatNum } from '@/util/number';
import {
  buildIconConfig,
  scaleIconSize,
  HEADING_ICON_BASE_SIZE,
} from '@/util/ship/shipIcons';
import { TRACK_COLORS } from '@/util/ship/shipStatus';

export default {
  name: 'SatelliteDatalog',
  mixins: [filterDeepLink],
  filterUrlGroups: ['asset', 'date'],
  components: {
    DataFilter,
  },
  data() {
    return {
      isLoading: false,
      loadSeq: 0,
      gpsData: [],
      rpmData: [],
      combinedData: [],
      mapService: null,
      trailLayer: null,
      dotTrailMarkers: [],
      shipMarker: null,
      headingMarker: null,
    };
  },
  computed: {
    currAsset() {
      const currMassId = this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
  },
  async mounted() {
    // Leaflet butuh CSS-nya sendiri, tapi gak ke-bundle otomatis di project ini —
    // tiap halaman yg pake map wajib inject manual (samain sama views/map/index.vue).
    // Tanpa ini, pane Leaflet jadi block-stacking biasa & tile/marker meleot jauh ke bawah.
    this.loadLeafletCSS();
    // Halaman ini nol referensi ke currAsset.summary — cuma butuh daftar device.
    await initAssetSummary(this.$store, {
      withDevices: true,
      skipSummary: true,
    });
    this.initMap();
    this.autoSelectAsset();
  },
  watch: {
    // Watch for route changes to ensure data is loaded when accessing SatelliteDatalog directly
    '$route.query.menu': {
      immediate: true,
      async handler(menu) {
        if (menu === 'SatelliteDatalog') {
          // Ensure assets are loaded before applying filter
          const assets = this.$store.getters['asset/assets'];
          if (!assets || assets.length === 0) {
            await initAssetSummary(this.$store, {
              withDevices: true,
              skipSummary: true,
            });
          }
        }
      },
    },
  },
  beforeDestroy() {
    this.cleanupMap();
  },
  methods: {
    autoSelectAsset() {
      const assets = this.$store.getters['asset/assets'];
      if (assets && assets.length > 0) {
        // Aset terpilih (mis. dari URL) dipertahankan kalau punya Satellite ID.
        const keptAsset = this.currAsset?.massSateliteId ? this.currAsset : null;
        const satelliteAsset = assets.find((asset) => asset.massSateliteId);
        const firstAsset = keptAsset || satelliteAsset || assets[0];

        if (firstAsset) {
          this.$store.dispatch('filter/setCurrentMassId', firstAsset.massId);

          // Auto apply filter after a short delay
          this.$nextTick(() => {
            this.onApplyFilter();
          });
        }
      }
    },

    loadLeafletCSS() {
      if (!document.querySelector('link[href="/style/leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/style/leaflet.css';
        document.head.appendChild(link);
      }
      // mapService pasang polylineMeasure control di initializeDefaultMap, butuh CSS-nya juga.
      if (!document.querySelector('link[href="/style/Leaflet.PolylineMeasure.css"]')) {
        const link2 = document.createElement('link');
        link2.rel = 'stylesheet';
        link2.href = '/style/Leaflet.PolylineMeasure.css';
        document.head.appendChild(link2);
      }
    },

    initMap() {
      // Create a wrapper component for MapService
      const mapWrapper = {
        $store: this.$store,
        ships: [],
        handlePolylineStart: () => {},
        handlePolylineChange: () => {},
        handlePolylineClear: () => {},
        handlePolylineFinish: () => {},
      };

      this.mapService = new MapService(mapWrapper);
      this.mapService.initializeDefaultMap('satelliteMap');
    },

    cleanupMap() {
      if (this.mapService) {
        this.mapService.destroy();
        this.mapService = null;
      }
    },

    async onApplyFilter() {
      // Penanda SEBELUM await; autoSelectAsset juga nembak ini lewat $nextTick.
      const seq = ++this.loadSeq;
      this.isLoading = true;
      try {
        const { start, end } = this.$store.getters['filter/date'];
        const startTimestamp = Math.floor(start.valueOf() / 1000);
        const endTimestamp = Math.floor(end.valueOf() / 1000);

        // Use massSateliteId from the selected asset
        const massSateliteId = this.currAsset?.massSateliteId;
        if (!massSateliteId) {
          this.$toast.error('Selected asset does not have a Satellite ID');
          return;
        }

        const deviceParam = {
          params: {
            device: [massSateliteId],
          },
        };

        // Fetch GPS and RPM data in parallel
        const [gpsResponse, rpmResponse] = await Promise.all([
          SeriesSvc.getAggregatedSatelliteGpsByPeriod(startTimestamp, endTimestamp, deviceParam),
          SeriesSvc.getAggregatedSatelliteRpmByPeriod(startTimestamp, endTimestamp, deviceParam),
        ]);
        if (seq !== this.loadSeq) return;

        this.gpsData = gpsResponse?.data?.data?.series || [];
        this.rpmData = rpmResponse?.data?.data?.series || [];

        this.combineData();
        this.updateMapTrail();
        this.syncFilterUrl();
      } catch (error) {
        if (seq !== this.loadSeq) return;
        console.error('Error fetching satellite data:', error);
        this.$toast.error('Failed to fetch data');
        this.gpsData = [];
        this.rpmData = [];
        this.combinedData = [];
      } finally {
        if (seq === this.loadSeq) {
          this.isLoading = false;
          if (this.$refs.dataFilter) this.$refs.dataFilter.markApplied();
        }
      }
    },

    combineData() {
      // Group RPM data by hour to match with GPS data (same hour logic as home page)
      const rpmByHour = {};
      this.rpmData.forEach((item) => {
        const hour = Math.floor(item.timestamp / 3600);
        if (!rpmByHour[hour]) {
          rpmByHour[hour] = {
            portRpm: null,
            stbRpm: null,
            datetime: item.datetime,
            timestamp: item.timestamp,
          };
        }
        if (item.type === 'PORT') {
          rpmByHour[hour].portRpm = item.rpm;
        } else if (item.type === 'STARBOARD') {
          rpmByHour[hour].stbRpm = item.rpm;
        }
      });

      // Combine GPS and RPM data by matching hour (ignore minutes/seconds)
      this.combinedData = this.gpsData.map((gpsItem) => {
        const gpsHour = Math.floor(gpsItem.timestamp / 3600);
        const matchingRpm = rpmByHour[gpsHour] || {};

        return {
          gpsDatetime: gpsItem.datetime,
          gpsTimestamp: gpsItem.timestamp,
          latitude: gpsItem.latitude,
          longitude: gpsItem.longitude,
          heading: gpsItem.track ?? gpsItem.heading ?? null,
          speed: gpsItem.speed,
          satellites: gpsItem.satellites,
          rpmDatetime: matchingRpm.datetime || gpsItem.datetime,
          portRpm: matchingRpm.portRpm ?? null,
          stbRpm: matchingRpm.stbRpm ?? null,
        };
      });
    },

    getShipIcon(asset) {
      // Sat datalog nampilin log historis, bukan status live kapal — selalu pake
      // status "online" biar konsisten sama convention sat mode di tempat lain
      // (lihat mapService.js createSatPopupContent/createSatDotTrailPopupContent).
      const { iconConfig } = buildIconConfig(asset, 'online');

      return {
        icon: L.icon({
          iconUrl: iconConfig.iconUrl,
          iconSize: iconConfig.iconSize,
          popupAnchor: [0, -41],
          tooltipAnchor: [-50, 50],
          className: 'ship-icon',
        }),
        hasHeadingIcon: iconConfig.hasHeadingIcon,
      };
    },

    updateMapTrail() {
      if (!this.mapService || !this.mapService.map) return;

      const map = this.mapService.map;

      // Clear existing layers
      if (this.trailLayer) {
        map.removeLayer(this.trailLayer);
      }
      this.dotTrailMarkers.forEach((marker) => map.removeLayer(marker));
      this.dotTrailMarkers = [];

      if (this.shipMarker) {
        map.removeLayer(this.shipMarker);
        this.shipMarker = null;
      }
      if (this.headingMarker) {
        map.removeLayer(this.headingMarker);
        this.headingMarker = null;
      }

      const validPoints = this.combinedData.filter(
        (p) => p.latitude && p.longitude && p.latitude !== 0 && p.longitude !== 0,
      );

      if (validPoints.length === 0) {
        this.$toast.info('No valid GPS coordinates available');
        return;
      }

      // 1. Create trail polyline (same style as home page sat mode - green color)
      const latlngs = validPoints.map((p) => [p.latitude, p.longitude]);
      this.trailLayer = L.polyline(latlngs, {
        color: TRACK_COLORS.online,
        weight: 4,
        zIndexOffset: -1000, // Keep trail below markers
      }).addTo(map);

      // 2. Create dot trail markers (skip the last point, same as home page)
      const seriesToRender = validPoints.slice(0, -1);
      const SatMarkerIcon = L.icon({
        iconUrl: markerOnline,
        iconSize: [35, 61],
        className: 'marker-icon',
      });

      seriesToRender.forEach((point, index) => {
        const marker = L.marker([point.latitude, point.longitude], {
          icon: SatMarkerIcon,
          rotationAngle: Number.isFinite(Number(point.heading)) ? Number(point.heading) : 0,
          rotationOrigin: '50% 50%',
          zIndexOffset: 1000, // Keep markers above trails
        });

        const popupContent = this.createSatDotTrailPopupContent(point, index);
        marker.bindPopup(popupContent);

        marker.on('popupopen', (e) => {
          e.target.setPopupContent(popupContent);
        });

        this.dotTrailMarkers.push(marker);
        marker.addTo(map);
      });

      // 3. Create ship marker at the latest position (using ship type icon like home page)
      const lastPoint = validPoints[validPoints.length - 1];
      const heading = Number.isFinite(Number(lastPoint.heading)) ? Number(lastPoint.heading) : 0;
      const { icon: ShipIcon, hasHeadingIcon } = this.getShipIcon(this.currAsset);

      this.shipMarker = L.marker([lastPoint.latitude, lastPoint.longitude], {
        icon: ShipIcon,
        // Icon silhouette detail (tug boat/LCT) gak boleh diputer langsung, cuma
        // heading arrow di atasnya yg muter — biar gambarnya gak jadi miring aneh.
        ...(hasHeadingIcon ? {} : { rotationAngle: heading, rotationOrigin: '50% 50%' }),
        zIndexOffset: 2000, // Keep ship marker above dot trail
      }).addTo(map);

      if (hasHeadingIcon) {
        this.headingMarker = L.marker([lastPoint.latitude, lastPoint.longitude], {
          icon: L.icon({
            iconUrl: headingOnline,
            iconSize: scaleIconSize(HEADING_ICON_BASE_SIZE),
            className: 'heading-icon',
          }),
          interactive: false,
          rotationAngle: heading,
          rotationOrigin: '50% 50%',
          zIndexOffset: 2100,
        }).addTo(map);
      }

      const shipPopupContent = this.createShipPopupContent(lastPoint, validPoints.length);
      this.shipMarker.bindPopup(shipPopupContent);

      // Sat mode: selalu tampil online (data historis, bukan status real-time kapal).
      this.shipMarker.bindTooltip(
        `<div class="ship-label ship-online">${this.currAsset?.massName || '-'}</div>`,
        { permanent: true, offset: [20, 10] },
      ).openTooltip();

      // Fit map to bounds
      map.fitBounds(this.trailLayer.getBounds(), { padding: [50, 50] });
    },

    // eslint-disable-next-line no-unused-vars
    createSatDotTrailPopupContent(data, index) {
      const timestamp = data?.gpsTimestamp;
      const datetime = timestamp ? moment(timestamp * 1000).format(DISPLAY_DATETIME) : '-';
      const latitude = data?.latitude;
      const longitude = data?.longitude;
      const speedValue = data?.speed ?? null; // backend provides speed in KNOT
      const headingDisplay = this.formatHeading(data?.heading);
      const portRpm = data?.portRpm;
      const starboardRpm = data?.stbRpm;

      const latDMS = latitude ? convertDecimalToDMS(latitude, true) : '-';
      const lngDMS = longitude ? convertDecimalToDMS(longitude, false) : '-';
      const coordinateDisplay = `${latDMS}, ${lngDMS}`;

      return `
        <div class="row pt-3">
          <div class="col-6 d-flex align-items-center"><h6>${this.currAsset?.massName || '-'}</h6></div>
          <div class="col-6 d-flex justify-content-end align-items-center pt-0"><span class="status online">online</span></div>
        </div>
        <hr style="margin: 12px 0;" />
        <div class="row"><div class="col-12"><p style="margin: 0; font-size: 13px"><b>Time: </b> ${datetime}</p></div></div>
        <hr style="margin: 12px 0;" />
        <div class="row info-section pb-2" style="margin-bottom: 8px;">
          <div class="col-12" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Coordinates</p><p style="font-weight: 1000; color: #1e1e1e; font-size: 13px">${coordinateDisplay}</p></div>
        </div>
        <div class="row info-section pb-2" style="margin-bottom: 8px;">
          <div class="col-6" style="height: 40px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Speed</p><p style="font-weight: 1000; color: #1e1e1e">${speedValue !== null ? `${formatNum(speedValue, 2)} knot` : '-'}</p></div>
          <div class="col-6" style="height: 40px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Heading</p><p style="font-weight: 1000; color: #1e1e1e">${headingDisplay}</p></div>
        </div>
        <div class="row info-section pb-2" style="margin-bottom: 8px;">
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Port RPM</p><p style="font-weight: 1000; color: #1e1e1e">${portRpm !== null ? portRpm : '-'}</p></div>
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px">Starboard RPM</p><p style="font-weight: 1000; color: #1e1e1e">${starboardRpm !== null ? starboardRpm : '-'}</p></div>
        </div>
      `;
    },

    createShipPopupContent(data, totalPoints) {
      const timestamp = data?.gpsTimestamp;
      const datetime = timestamp ? moment(timestamp * 1000).format(DISPLAY_DATETIME) : '-';
      const latitude = data?.latitude;
      const longitude = data?.longitude;
      const portRpm = data?.portRpm;
      const starboardRpm = data?.stbRpm;
      const speed = data?.speed ?? null; // backend provides speed in KNOT
      const headingDisplay = this.formatHeading(data?.heading);
      const satellites = data?.satellites;

      const latDMS = latitude ? convertDecimalToDMS(latitude, true) : '-';
      const lngDMS = longitude ? convertDecimalToDMS(longitude, false) : '-';
      const coordinateDisplay = `${latDMS}, ${lngDMS}`;

      return `
        <div class="row pt-3">
          <div class="col-6 d-flex align-items-center"><h5>${this.currAsset?.massName || '-'}</h5></div>
          <div class="col-6 d-flex justify-content-end align-items-center pt-0"><h5 class="status online">online</h5></div>
        </div>
        <hr style="margin: 12px 0;" />
        <div class="row"><div class="col-12"><p style="margin: 0; font-size: 14px"><b>Last Data: </b> ${datetime}</p></div></div>
        <hr style="margin: 12px 0;" />
        <div class="row info-section pb-2">
          <div class="col-12" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Coordinates</p><p style="font-weight: 1000; color: #1e1e1e">${coordinateDisplay}</p></div>
        </div>
        <div class="row info-section pb-2">
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Port RPM</p><p style="font-weight: 1000; color: #1e1e1e">${portRpm !== null ? Math.floor(portRpm) : '-'}</p></div>
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px">Starboard RPM</p><p style="font-weight: 1000; color: #1e1e1e">${starboardRpm !== null ? Math.floor(starboardRpm) : '-'}</p></div>
        </div>
        <div class="row info-section pb-2">
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Speed</p><p style="font-weight: 1000; color: #1e1e1e">${speed !== null ? `${formatNum(speed, 2)} knot` : '-'}</p></div>
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px">Heading</p><p style="font-weight: 1000; color: #1e1e1e">${headingDisplay}</p></div>
        </div>
        <div class="row info-section pb-2">
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px">Satellites</p><p style="font-weight: 1000; color: #1e1e1e">${satellites ?? '-'}</p></div>
          <div class="col-6" style="height: 60px;"><p style="margin-top: 0; margin-bottom: -10px; color: #1e1e1e">Trail Points</p><p style="font-weight: 1000; color: #1e1e1e">${totalPoints} points</p></div>
        </div>
      `;
    },

    formatHeading(value) {
      return Number.isFinite(Number(value)) ? `${formatNum(Number(value), 2)}\u00B0` : '-';
    },

    formatToBrowserTime(datetimeString) {
      if (!datetimeString) return '-';
      return fromApiDatetime(datetimeString).format(DISPLAY_DATETIME);
    },
  },
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

#satelliteMap {
  z-index: 1;
}

/* Custom scrollbar for table */
.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Make table header sticky */
.sticky-top {
  background-color: #f8f9fa;
}

/* Responsive adjustments */
@media (max-width: 991px) {
  .row > div {
    margin-bottom: 1rem;
  }
}
</style>
